import type { InputTextProps } from '@carboncopyco/ui';
import { InputText } from '@carboncopyco/ui';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { InputField } from '../InputField';

export interface InputTextFieldProps
  extends Pick<
    InputTextProps,
    | 'name'
    | 'value'
    | 'disabled'
    | 'autoFocus'
    | 'onFocus'
    | 'multiline'
    | 'multilineOptions'
    | 'placeholder'
    | 'maxLength'
  > {
  onChange: (value: string) => void;
  label: string;
  errorMessage?: string;
  linkLabel?: string;
  linkUrl?: string;
  onLearnMoreClick?: (link: string) => void;
}

export const InputTextField = ({
  name,
  label,
  linkLabel,
  linkUrl = 'https://www.demostack.com',
  onLearnMoreClick,
  value,
  autoFocus,
  disabled,
  onChange,
  onFocus,
  multiline,
  multilineOptions,
  errorMessage,
  placeholder,
  maxLength,
}: InputTextFieldProps) => {
  const inputRef = useRef<HTMLInputElement>();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange(val);
  };

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  return (
    <InputField
      label={label}
      name={name}
      error={
        errorMessage && (
          <>
            <span>{errorMessage} </span>
            {onLearnMoreClick && (
              <a
                className="underline"
                href={linkUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  if (onLearnMoreClick) {
                    e.preventDefault();
                    onLearnMoreClick(linkUrl);
                  }
                }}
              >
                {linkLabel}
              </a>
            )}
          </>
        )
      }
    >
      <InputText
        ref={inputRef}
        autoFocus={autoFocus}
        onFocus={onFocus}
        name={name}
        disabled={disabled}
        size="lg"
        multiline={multiline}
        placeholder={placeholder}
        multilineOptions={multilineOptions}
        value={value}
        maxLength={maxLength}
        onChange={handleChange}
        state={errorMessage ? 'error' : 'normal'}
      />
    </InputField>
  );
};
